import { Component, DestroyRef, inject } from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@env';
import {
  SystemErrorBottomSheetData,
  SystemErrorModalComponent,
} from './shared/ui/system-error-modal/system-error-modal.component';
import { SystemErrorModalService } from '@services/system-error-modal.service';
import {
  NetworkOfflineBottomSheetData,
  NetworkOfflineModalComponent,
} from './shared/ui/network-offline-modal/network-offline-modal.component';
import { NetworkOfflineModalService } from '@services/network-offline-modal.service';
import { AnalyticsService } from '@services/analytics.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterModule],
  templateUrl: './app.html',
})
export class AppComponent {
  private readonly systemErrorModal = inject(SystemErrorModalService);
  private readonly networkOfflineModal = inject(NetworkOfflineModalService);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly analytics = inject(AnalyticsService);

  private systemErrorSheetRef: MatBottomSheetRef<SystemErrorModalComponent> | null = null;
  private networkOfflineSheetRef: MatBottomSheetRef<NetworkOfflineModalComponent> | null = null;

  constructor() {
    // Inyectamos Contentsquare en tiempo de ejecución sólo si hay una URL configurada
    // y si estamos en el cliente (evitar problemas con SSR).
    const contentsquareUrl = environment.contentsquareScriptUrl;
    if (contentsquareUrl && typeof document !== 'undefined') {
      this.injectScriptOnce(contentsquareUrl);
    }

    // Inyectar Google Maps API dinámicamente desde el environment
    const googleMapsApiKey = (environment as any).googleMapsApiKey;
    if (googleMapsApiKey && typeof document !== 'undefined') {
      const googleMapsUrl = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
      this.injectScriptOnce(googleMapsUrl);
    }

    this.setupNetworkStatusListeners();
    this.setupGlobalErrorSheets();
    this.setupScrollRestoration();
  }

  private setupGlobalErrorSheets(): void {
    this.systemErrorModal.state$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state.open) {
          this.openSystemErrorSheet({
            title: state.title,
            body: state.body,
            confirmLabel: state.confirmLabel,
          });
          return;
        }

        this.systemErrorSheetRef?.dismiss();
        this.systemErrorSheetRef = null;
      });

    this.networkOfflineModal.state$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state.open) {
          this.openNetworkOfflineSheet({
            title: state.title,
            message: state.message,
            retryLabel: state.retryLabel,
          });
          return;
        }

        this.networkOfflineSheetRef?.dismiss();
        this.networkOfflineSheetRef = null;
      });
  }

  private openSystemErrorSheet(data: SystemErrorBottomSheetData): void {
    if (this.systemErrorSheetRef) return;

    const ref = this.bottomSheet.open(SystemErrorModalComponent, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'system-error-bottom-sheet',
      data,
    });

    this.systemErrorSheetRef = ref;
    ref
      .afterDismissed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.systemErrorSheetRef === ref) {
          this.systemErrorSheetRef = null;
        }
        this.onSystemErrorDismiss();
      });
  }

  private openNetworkOfflineSheet(data: NetworkOfflineBottomSheetData): void {
    if (this.networkOfflineSheetRef) return;

    const ref = this.bottomSheet.open(NetworkOfflineModalComponent, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'network-offline-bottom-sheet',
      data,
    });

    this.networkOfflineSheetRef = ref;
    ref
      .afterDismissed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reason) => {
        if (this.networkOfflineSheetRef === ref) {
          this.networkOfflineSheetRef = null;
        }

        if (reason === 'retry') {
          this.onNetworkRetry();
          return;
        }

        this.onNetworkOfflineClose();
      });
  }

  private setupScrollRestoration() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        const navEnd = event as NavigationEnd;
        this.viewportScroller.scrollToPosition([0, 0]);
        this.analytics.trackPage(navEnd.urlAfterRedirects);
      });
  }

  private injectScriptOnce(url: string) {
    try {
      // Evitar añadir el script más de una vez
      if (typeof document === 'undefined') return;
      if (document.querySelector(`script[src="${url}"]`)) return;
      const s = document.createElement('script');
      s.src = url;
      s.async = true;
      document.head.appendChild(s);
    } catch (e) {
      // No queremos romper la app si algo falla con la inyección
      // puedes enviar esto a un logger si lo deseas
    }
  }

  private setupNetworkStatusListeners(): void {
    if (typeof window === 'undefined') return;

    fromEvent(window, 'offline')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.networkOfflineModal.show());

    fromEvent(window, 'online')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.networkOfflineModal.close());

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      queueMicrotask(() => this.networkOfflineModal.show());
    }
  }

  onSystemErrorDismiss(): void {
    this.systemErrorModal.close();
  }

  onNetworkOfflineClose(): void {
    this.networkOfflineModal.close();
  }

  onNetworkRetry(): void {
    this.networkOfflineModal.emitRetry();
    if (typeof navigator === 'undefined' || navigator.onLine) {
      this.networkOfflineModal.close();
      return;
    }

    // Keep the feedback visible when retry is pressed while still offline.
    this.networkOfflineModal.show();
  }
}
